import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { CheckIcon, CopyIcon } from '@phosphor-icons/react';
import { Focusable } from 'react-aria-components';
import { cn, copyToClipboard, Tooltip } from '@skupreme/shared/ui';
import { ToastMessage } from '@utility/toast';

export interface ITextCopyToClipboardProps {
  text?: string;
  copiedText?: string;
  iconSize?: number;
  position?: 'left' | 'right';
  className?: string;
  /** Clicking the text copies too, not only the icon. */
  copyText?: boolean;
  showText?: boolean;
  showIcon?: boolean;
  /** Copied instead of `text` when set. */
  copyTextValue?: string;
  copyTooltip?: string;
  textSize?: number | string;
}

const CONFIRMATION_MS = 1500;

/** Text with a copy icon that appears on hover; "Copied" confirms it. */
export function TextCopyToClipboard({
  text,
  copiedText,
  iconSize = 16,
  position = 'right',
  className = '',
  copyText = true,
  showText = true,
  showIcon = true,
  copyTextValue = '',
  textSize,
  copyTooltip = '',
}: ITextCopyToClipboardProps) {
  const [isCopied, setIsCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => () => clearTimeout(timer.current), []);

  if (!text) return null;

  const copy = async (e: React.SyntheticEvent) => {
    /* Copy targets sit in clickable rows and links, which must not fire too. */
    e.stopPropagation();
    e.preventDefault();

    const done = await copyToClipboard(copyTextValue || text);

    clearTimeout(timer.current);
    /* A refused write also takes back the "Copied" of an earlier one. */
    setIsCopied(done);
    if (!done) {
      ToastMessage.error('Something went wrong while copying.');
      return;
    }
    timer.current = setTimeout(() => setIsCopied(false), CONFIRMATION_MS);
  };

  const icon = (
    <Tooltip
      content={isCopied ? 'Copied' : copyTooltip}
      isOpen={isCopied || undefined}
      isDisabled={!isCopied && !copyTooltip}
      asChild
    >
      <Focusable>
        {/* biome-ignore lint/a11y/useSemanticElements: a <button> cannot nest in the buttons and links this sits in */}
        <span
          role="button"
          tabIndex={0}
          aria-label={copyTooltip || 'Copy'}
          onClick={copy}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') copy(e);
          }}
          className={cn(
            'sk:flex sk:cursor-copy sk:items-center sk:outline-none',
            'sk:opacity-0 sk:group-hover:opacity-100 sk:focus-visible:opacity-100 sk:[@media(hover:none)]:opacity-100',
            isCopied && 'sk:opacity-100',
            position === 'left' ? 'sk:mr-1' : 'sk:ml-1',
          )}
        >
          {isCopied ? (
            <CheckIcon size={iconSize} className="sk:text-success" />
          ) : (
            <CopyIcon size={iconSize} />
          )}
        </span>
      </Focusable>
    </Tooltip>
  );

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: the icon is the keyboard's way to copy; the text click is a mouse shortcut
    // biome-ignore lint/a11y/noStaticElementInteractions: as above
    <div
      style={textSize ? { fontSize: textSize } : undefined}
      className={cn('sk:group sk:flex sk:items-center', className)}
      onClick={copyText ? copy : undefined}
      onMouseLeave={() => setIsCopied(false)}
    >
      {showIcon && position === 'left' && icon}
      {showText ? (copiedText && isCopied ? copiedText : text) : ''}
      {showIcon && position === 'right' && icon}
    </div>
  );
}

export default TextCopyToClipboard;
